import { useEffect, useState } from "react";
import { FaAngleDown, FaBars, FaTimes, FaUserCircle } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Components
import Cash from "./Cash";
import Contract from "./Contract";
import Expenses from "./Expenses";
import Menu from "./Menu";
import NoContract from "./NoContract";
import Orders from "./Order";
import Payments from "./Payment";
import Performance from "./Performance";
import Purchase from "./Purchase";
import Revenue from "./Revenue";
import Sales from "./Sales";
import Transfer from "./Transfer";

// Charts & Exports
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import "./styles/Admin.css";

const STAFF_API = "https://okna.co.tz/mrms/Staff.php";
const ORDERS_API = "https://okna.co.tz/mrms/Orders.php";
const PAYMENTS_API = "https://okna.co.tz/mrms/Payment.php";
const EXPENSES_API = "https://okna.co.tz/mrms/Expenses.php";
const PURCHASES_API = "https://okna.co.tz/mrms/Purchase.php";

const Admin = () => {
  const [activeNav, setActiveNav] = useState("Dashboard");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [staffName, setStaffName] = useState("");
  const [role, setRole] = useState("");
  const [dashboardData, setDashboardData] = useState({
    ordersToday: 0,
    totalRevenue: 0,
    pendingPayments: 0,
    activeStaff: 0,
    inventoryItems: 0,
    recentExpenses: 0,
    recentPurchases: 0
  });
  const [realtimeMetrics, setRealtimeMetrics] = useState({
    revenue: 0,
    orders: 0,
    customers: 0,
  });
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  // Get token from localStorage
  const getToken = () => localStorage.getItem("token");

  // Helper function for API calls with token in URL and headers
  const fetchWithToken = async (url, options = {}) => {
    const token = getToken();
    if (!token) {
      throw new Error("No token found");
    }
    
    // Add token to URL as query parameter
    const separator = url.includes('?') ? '&' : '?';
    const urlWithToken = `${url}${separator}token=${token}`;
    
    const response = await fetch(urlWithToken, {
      ...options,
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        ...options.headers
      }
    });
    
    // Handle 401 Unauthorized
    if (response.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("staff");
      toast.error("Session expired. Please login again.");
      navigate("/");
      throw new Error("Unauthorized");
    }
    
    return response;
  };

  // ================= AUTH =================
  useEffect(() => {
    const token = getToken();
    const staff = JSON.parse(localStorage.getItem("staff") || "{}");

    if (!token || !staff || staff.role !== "Admin") {
      navigate("/");
      return;
    }

    setStaffName(staff.full_name);
    setRole(staff.role);

    // Fetch dashboard data
    fetchDashboardData();
    
    // Real-time metrics interval
    const interval = setInterval(() => {
      updateRealtimeMetrics();
    }, 10000);

    return () => clearInterval(interval);
  }, [navigate]);

  // ================= ROUTE DETECTION =================
  useEffect(() => {
    const path = location.pathname;

    if (path.includes("/admin/orders")) setActiveNav("Order Management");
    else if (path.includes("/admin/purchases")) setActiveNav("Purchases");
    else if (path.includes("/admin/payments")) setActiveNav("Payments");
    else if (path.includes("/admin/expenses")) setActiveNav("Expenses");
    else if (path.includes("/admin/menu")) setActiveNav("Menu Management");
    else if (path.includes("/admin/employees/contract")) setActiveNav("Contract Employees");
    else if (path.includes("/admin/employees/nocontract")) setActiveNav("Non-Contract Employees");
    else if (path.includes("/admin/transfer")) setActiveNav("Transfer");
    else if (path.includes("/admin/reports/sales")) setActiveNav("Sales Report");
    else if (path.includes("/admin/reports/performance")) setActiveNav("Waitress Report");
    else if (path.includes("/admin/reports/cash")) setActiveNav("Cash Balance");
    else if (path.includes("/admin/reports/revenue")) setActiveNav("Revenue Report");
    else setActiveNav("Dashboard");
  }, [location]);

  // ================= FETCH DASHBOARD DATA =================
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) {
        toast.error("Authentication required");
        navigate("/");
        return;
      }

      // Fetch all data in parallel with token in URL
      const [ordersRes, paymentsRes, expensesRes, purchaseRes, staffRes] = await Promise.all([
        fetch(`${ORDERS_API}?admin=true&token=${token}`, {
          headers: { "Authorization": `Bearer ${token}` }
        }),
        fetch(`${PAYMENTS_API}?token=${token}`, {
          headers: { "Authorization": `Bearer ${token}` }
        }),
        fetch(`${EXPENSES_API}?token=${token}`, {
          headers: { "Authorization": `Bearer ${token}` }
        }),
        fetch(`${PURCHASES_API}?token=${token}`, {
          headers: { "Authorization": `Bearer ${token}` }
        }),
        fetch(`${STAFF_API}?token=${token}`, {
          headers: { "Authorization": `Bearer ${token}` }
        })
      ]);

      // Check for 401 responses
      if (ordersRes.status === 401 || paymentsRes.status === 401 || 
          expensesRes.status === 401 || purchaseRes.status === 401 || 
          staffRes.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("token");
        localStorage.removeItem("staff");
        navigate("/");
        return;
      }

      // Process responses with error handling
      let orders = [];
      let payments = [];
      let expenses = [];
      let purchase = [];
      let staff = [];

      try {
        if (ordersRes.ok) orders = await ordersRes.json();
        if (paymentsRes.ok) payments = await paymentsRes.json();
        if (expensesRes.ok) expenses = await expensesRes.json();
        if (purchaseRes.ok) purchase = await purchaseRes.json();
        if (staffRes.ok) staff = await staffRes.json();
      } catch (parseError) {
        console.error("Error parsing JSON:", parseError);
      }

      // Calculate today's date
      const today = new Date().toISOString().split('T')[0];
      
      // Calculate metrics
      const ordersToday = Array.isArray(orders) ? 
        orders.filter(order => order.created_at?.includes(today)).length : 0;
      
      const totalRevenue = Array.isArray(payments) ?
        payments
          .filter(p => p.status === 'PAID' || p.status === 'APPROVED')
          .reduce((sum, p) => sum + (parseFloat(p.total_amount) || 0), 0) : 0;
      
      const pendingPayments = Array.isArray(payments) ?
        payments.filter(p => p.status === 'PENDING').length : 0;
      
      const activeStaff = Array.isArray(staff) ?
        staff.filter(s => s.active === 'yes' || s.active === 1).length : 0;
      
      // Calculate today's expenses
      const recentExpenses = Array.isArray(expenses) ?
        expenses
          .filter(e => e.created_at?.includes(today))
          .reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0) : 0;
      
      // Calculate today's purchases
      const recentPurchases = Array.isArray(purchase) ?
        purchase
          .filter(p => {
            const purchaseDate = p.created_at || p.purchase_date;
            return purchaseDate?.includes(today);
          })
          .reduce((sum, p) => sum + (parseFloat(p.amount) || parseFloat(p.total_price) || 0), 0) : 0;

      setDashboardData({
        ordersToday,
        totalRevenue,
        pendingPayments,
        activeStaff,
        inventoryItems: 58,
        recentExpenses,
        recentPurchases
      });

      // Initialize real-time metrics
      setRealtimeMetrics({
        revenue: totalRevenue,
        orders: ordersToday,
        customers: ordersToday * 2
      });

      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error(error.message || "Failed to load dashboard data");
      setLoading(false);
    }
  };

  // ================= UPDATE REALTIME METRICS =================
  const updateRealtimeMetrics = () => {
    setRealtimeMetrics(prev => ({
      ...prev,
      revenue: prev.revenue + Math.floor(Math.random() * 5000),
      orders: prev.orders + Math.floor(Math.random() * 2),
      customers: prev.customers + Math.floor(Math.random() * 3)
    }));
  };

  // ================= NAVIGATION FUNCTIONS =================
  const toggleMobileMenu = () => setShowMobileMenu(!showMobileMenu);

  const toggleSubMenu = (menu) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const handleLogout = () => {
    toast.info("Logging out...");
    setTimeout(() => {
      localStorage.removeItem("token");
      localStorage.removeItem("staff");
      navigate("/");
    }, 1000);
  };

  // Format currency in TZS
  const formatCurrency = (amount) => 
    `TZS ${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;

  // Chart data based on actual revenue
  const chartData = [
    { time: "9AM", value: dashboardData.totalRevenue * 0.2 || 0 },
    { time: "12PM", value: dashboardData.totalRevenue * 0.35 || 0 },
    { time: "3PM", value: dashboardData.totalRevenue * 0.25 || 0 },
    { time: "6PM", value: dashboardData.totalRevenue * 0.2 || 0 }
  ];

  return (
    <div className="admindash-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* SIDEBAR */}
      <div className={`admindash-sidebar ${showMobileMenu ? "mobile-active" : ""}`}>
        <div className="admindash-sidebar-header">
          <h2>Admin Dashboard</h2>
          <button className="admindash-close-mobile" onClick={toggleMobileMenu}>
            <FaTimes />
          </button>
        </div>

        <ul className="admindash-nav-links">
          <li
            className={activeNav === "Dashboard" ? "active" : ""}
            onClick={() => { 
              setActiveNav("Dashboard"); 
              navigate("/admin"); 
              setShowMobileMenu(false); 
            }}
          >
            Dashboard
          </li>

          <li
            className={activeNav === "Order Management" ? "active" : ""}
            onClick={() => { 
              setActiveNav("Order Management"); 
              navigate("/admin/orders"); 
              setShowMobileMenu(false); 
            }}
          >
            Orders
          </li>

          <li
            className={activeNav === "Payments" ? "active" : ""}
            onClick={() => { 
              setActiveNav("Payments"); 
              navigate("/admin/payments"); 
              setShowMobileMenu(false); 
            }}
          >
            Payments
          </li>

          {/* EMPLOYEE MANAGEMENT */}
          <li>
            <div
              className={`admindash-submenu-title ${activeNav.includes("Employees") ? "active" : ""}`}
              onClick={() => toggleSubMenu("Employee Management")}
            >
              Employee <FaAngleDown />
            </div>
            {expandedMenus["Employee Management"] && (
              <ul className="admindash-submenu">
                <li
                  className={activeNav === "Contract Employees" ? "active" : ""}
                  onClick={() => { 
                    setActiveNav("Contract Employees"); 
                    navigate("/admin/employees/contract"); 
                    setShowMobileMenu(false); 
                  }}
                >
                  Contract
                </li>
                <li
                  className={activeNav === "Non-Contract Employees" ? "active" : ""}
                  onClick={() => { 
                    setActiveNav("Non-Contract Employees"); 
                    navigate("/admin/employees/nocontract"); 
                    setShowMobileMenu(false); 
                  }}
                >
                  Non-Contract
                </li>
              </ul>
            )}
          </li>

          <li
            className={activeNav === "Expenses" ? "active" : ""}
            onClick={() => { 
              setActiveNav("Expenses"); 
              navigate("/admin/expenses"); 
              setShowMobileMenu(false); 
            }}
          >
            Expenses
          </li>

          <li
            className={activeNav === "Purchases" ? "active" : ""}
            onClick={() => { 
              setActiveNav("Purchases"); 
              navigate("/admin/purchases"); 
              setShowMobileMenu(false); 
            }}
          >
            Purchases
          </li>

          <li
            className={activeNav === "Menu Management" ? "active" : ""}
            onClick={() => { 
              setActiveNav("Menu Management"); 
              navigate("/admin/menu"); 
              setShowMobileMenu(false); 
            }}
          >
            Menu Management
          </li>

          <li
            className={activeNav === "Transfer" ? "active" : ""}
            onClick={() => { 
              setActiveNav("Transfer"); 
              navigate("/admin/transfer"); 
              setShowMobileMenu(false); 
            }}
          >
            Transfer
          </li>

          {/* REPORTS */}
          <li>
            <div
              className={`admindash-submenu-title ${activeNav.includes("Report") ? "active" : ""}`}
              onClick={() => toggleSubMenu("Report")}
            >
              Reports <FaAngleDown />
            </div>
            {expandedMenus["Report"] && (
              <ul className="admindash-submenu">
                <li
                  className={activeNav === "Sales Report" ? "active" : ""}
                  onClick={() => { 
                    setActiveNav("Sales Report"); 
                    navigate("/admin/reports/sales"); 
                    setShowMobileMenu(false); 
                  }}
                >
                  Sales
                </li>
                <li
                  className={activeNav === "Waitress Report" ? "active" : ""}
                  onClick={() => { 
                    setActiveNav("Waitress Report"); 
                    navigate("/admin/reports/performance"); 
                    setShowMobileMenu(false); 
                  }}
                >
                  Performance
                </li>
                <li
                  className={activeNav === "Cash Balance" ? "active" : ""}
                  onClick={() => { 
                    setActiveNav("Cash Balance"); 
                    navigate("/admin/reports/cash"); 
                    setShowMobileMenu(false); 
                  }}
                >
                  Cash Balance
                </li>
                <li
                  className={activeNav === "Revenue Report" ? "active" : ""}
                  onClick={() => { 
                    setActiveNav("Revenue Report"); 
                    navigate("/admin/reports/revenue"); 
                    setShowMobileMenu(false); 
                  }}
                >
                  Revenue
                </li>
              </ul>
            )}
          </li>
        </ul>
      </div>

      {/* MAIN CONTENT */}
      <div className="admindash-main-content">
        <div className="admindash-top-header">
          <button className="admindash-mobile-menu-btn" onClick={toggleMobileMenu}>
            <FaBars />
          </button>
          <h1>MARAFIKI RESTAURANT MANAGEMENT SYSTEM</h1>
          <div className="admindash-user-profile" onClick={() => setShowUserDropdown(!showUserDropdown)}>
            <FaUserCircle size={30} />
            <span className="admindash-green-dot"></span>
            <span className="admindash-username">{staffName}</span>
            {showUserDropdown && (
              <div className="admindash-user-dropdown">
                <button onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>
        </div>

        <div className="admindash-content">
          {/* DASHBOARD */}
          {activeNav === "Dashboard" && (
            <div className="admindash-dashboard-cards">
              {loading ? (
                <div className="admindash-loading">
                  <div className="admindash-spinner"></div>
                  <p>Loading dashboard data...</p>
                </div>
              ) : (
                <>
                  <div className="admindash-card">
                    <h3>Orders Today</h3>
                    <p className="admindash-card-value">{dashboardData.ordersToday}</p>
                  </div>
                  
                  <div className="admindash-card">
                    <h3>Total Revenue</h3>
                    <p className="admindash-card-value">{formatCurrency(dashboardData.totalRevenue)}</p>
                  </div>
                  
                  <div className="admindash-card">
                    <h3>Pending Payments</h3>
                    <p className="admindash-card-value">{dashboardData.pendingPayments}</p>
                  </div>
                  
                  <div className="admindash-card">
                    <h3>Active Staff</h3>
                    <p className="admindash-card-value">{dashboardData.activeStaff}</p>
                  </div>
                  
                  <div className="admindash-card">
                    <h3>Today's Expenses</h3>
                    <p className="admindash-card-value expense">{formatCurrency(dashboardData.recentExpenses)}</p>
                  </div>
                  
                  <div className="admindash-card">
                    <h3>Today's Purchases</h3>
                    <p className="admindash-card-value purchase">{formatCurrency(dashboardData.recentPurchases)}</p>
                  </div>

                  {/* REAL-TIME METRICS */}
                  <div className="admindash-realtime-metrics">
                    <h3>Real-Time Metrics</h3>
                    <div className="admindash-metric-item">
                      <span>Revenue:</span>
                      <span className="admindash-metric-value">{formatCurrency(realtimeMetrics.revenue)}</span>
                    </div>
                    <div className="admindash-metric-item">
                      <span>Orders:</span>
                      <span className="admindash-metric-value">{realtimeMetrics.orders}</span>
                    </div>
                    <div className="admindash-metric-item">
                      <span>Customers:</span>
                      <span className="admindash-metric-value">{realtimeMetrics.customers}</span>
                    </div>
                  </div>

                  {/* SALES CHART */}
                  {dashboardData.totalRevenue > 0 && (
                    <div className="admindash-chart-container">
                      <h3>Today's Sales Trend</h3>
                      <div style={{ width: "100%", height: 250 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData}>
                            <XAxis dataKey="time" />
                            <YAxis />
                            <Tooltip formatter={(value) => [formatCurrency(value), 'Sales']} />
                            <Line type="monotone" dataKey="value" stroke="#1abc9c" strokeWidth={2} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeNav === "Order Management" && <Orders />}
          {activeNav === "Purchases" && <Purchase />}
          {activeNav === "Payments" && <Payments />}
          {activeNav === "Expenses" && <Expenses />}
          {activeNav === "Menu Management" && <Menu />}
          {activeNav === "Contract Employees" && <Contract />}
          {activeNav === "Non-Contract Employees" && <NoContract />}
          {activeNav === "Transfer" && <Transfer />}

          {/* REPORTS */}
          {activeNav === "Sales Report" && <Sales />}
          {activeNav === "Waitress Report" && <Performance />}
          {activeNav === "Cash Balance" && <Cash />}
          {activeNav === "Revenue Report" && <Revenue />}
        </div>
      </div>
    </div>
  );
};

export default Admin;